import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { formatFounderTitle } from '../utils/textUtils';
import { getProfileDimensions } from '../utils/profileDimensions';

// Styled components
const DeepDiveContainer = styled(motion.div)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.9) 0%,
    rgba(18, 18, 18, 0.8) 50%,
    rgba(var(--primary-rgb), 0.05) 100%
  );
  border-radius: var(--border-radius);
  padding: 2.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(var(--primary-rgb), 0.15);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
    border-radius: 1px;
  }
`;

const InsightCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 1.8rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const InsightTitle = styled.h3`
  font-size: 1.2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:before {
    content: '▶';
    font-size: 0.8rem;
    opacity: 0.7;
  }
`;

const InsightContent = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1.05rem;
  margin: 0;
`;

const StrengthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const StrengthItem = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  padding: 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const StrengthLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const StrengthValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  
  span {
    color: var(--primary-color);
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const StrengthBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.value}%;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
    border-radius: 3px;
    transition: width 1.5s ease-out;
  }
`;

const DeepDiveSection = ({ profile, founderType }) => {
  if (!profile || !founderType) {
    return null;
  }

  const primaryScore = profile.percentages[profile.primaryType] || 0;
  
  // Generate dynamic insights based on founder type
  const generateInsights = () => {
    if (!founderType) {
      return {
        cognitiveStyle: {
          title: "Cognitive decision-making style",
          content: "Your thinking pattern and decision-making approach based on your founder profile."
        },
        leadershipDynamics: {
          title: "Leadership & team dynamics",
          content: "How you naturally lead and work with teams based on your founder personality."
        },
        businessTiming: {
          title: "Optimal business timing & context",
          content: "The business environments and timing where your founder type excels."
        }
      };
    }

    return {
      cognitiveStyle: {
        title: "Cognitive decision-making style",
        content: `Your ${primaryScore}% ${formatFounderTitle(founderType.title).replace('The ', '')} profile indicates you process information through ${
          founderType.title === 'The Analyst' ? 'systematic analysis and data-driven logic' :
          founderType.title === 'The Visionary' ? 'intuitive pattern recognition and future-focused thinking' :
          founderType.title === 'The Innovator' ? 'creative synthesis and unconventional connections' :
          founderType.title === 'The Executor' ? 'practical evaluation and action-oriented assessment' :
          founderType.title === 'The Strategist' ? 'comprehensive planning and risk-benefit analysis' :
          'relationship-focused evaluation and collaborative decision-making'
        }. This gives you a natural advantage in situations requiring ${founderType.title === 'The Analyst' ? 'complex problem-solving and quantitative analysis' : founderType.title === 'The Visionary' ? 'breakthrough thinking and paradigm shifts' : founderType.title === 'The Innovator' ? 'creative solutions and rapid adaptation' : founderType.title === 'The Executor' ? 'efficient execution and reliable delivery' : founderType.title === 'The Strategist' ? 'long-term planning and competitive positioning' : 'team alignment and stakeholder management'}.`
      },
      leadershipDynamics: {
        title: "Leadership & team dynamics",
        content: `As a dominant ${formatFounderTitle(founderType.title).replace('The ', '')}, you naturally lead through ${
          founderType.title === 'The Visionary' ? 'inspiration and compelling future narratives, energizing teams around ambitious goals' :
          founderType.title === 'The Executor' ? 'clear direction and consistent follow-through, building trust through reliable delivery' :
          founderType.title === 'The Innovator' ? 'creative problem-solving and encouraging experimentation, fostering innovation culture' :
          founderType.title === 'The Analyst' ? 'evidence-based reasoning and logical frameworks, ensuring informed decision-making' :
          founderType.title === 'The Strategist' ? 'systematic planning and anticipating challenges, providing stability and direction' :
          'relationship building and emotional intelligence, creating strong team cohesion'
        }. Your team members likely appreciate your ${founderType.strengths[0].toLowerCase()} and respond well to your ${founderType.strengths[1].toLowerCase()}. However, be mindful of ${founderType.challenges[0].toLowerCase()} which can sometimes impact team dynamics.`
      },
      businessTiming: {
        title: "Optimal business timing & context",
        content: `Your founder profile is particularly advantageous in ${
          founderType.title === 'The Visionary' ? 'emerging markets, disruptive innovation cycles, and early-stage ventures requiring clear direction' :
          founderType.title === 'The Executor' ? 'scaling operations, market penetration phases, and situations requiring operational excellence' :
          founderType.title === 'The Innovator' ? 'product development cycles, creative industries, and markets requiring differentiation' :
          founderType.title === 'The Analyst' ? 'data-rich environments, complex markets, and ventures requiring analytical rigor' :
          founderType.title === 'The Strategist' ? 'competitive markets, long-term planning scenarios, and complex business environments' :
          'relationship-driven businesses, service industries, and ventures requiring strong partnerships'
        }. Consider timing your major initiatives during periods when these contextual factors align with market conditions. Your natural ${founderType.strengths[0].toLowerCase()} will be most valuable during ${founderType.title === 'The Visionary' ? 'industry transformation periods' : founderType.title === 'The Executor' ? 'rapid growth phases' : founderType.title === 'The Innovator' ? 'product innovation cycles' : founderType.title === 'The Analyst' ? 'market uncertainty periods' : founderType.title === 'The Strategist' ? 'competitive landscape shifts' : 'team building and partnership development phases'}.`
      }
    };
  };

  const insights = generateInsights();
  const dimensions = getProfileDimensions(profile.percentages);

  return (
    <DeepDiveContainer
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <SectionTitle>Deep profile analysis</SectionTitle>
      <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: 1.6 }}>
        Dimension scores are calculated from your quiz answers, as blends of your six founder modes.
      </p>
      {Object.values(insights).map((insight, index) => (
        <InsightCard key={index}>
          <InsightTitle>{insight.title}</InsightTitle>
          <InsightContent>{insight.content}</InsightContent>
        </InsightCard>
      ))}
      
      <StrengthsGrid>
        {dimensions.map((dim) => (
          <StrengthItem key={dim.id}>
            <StrengthLabel>{dim.label}</StrengthLabel>
            <StrengthValue>
              <span>{dim.value}%</span>
            </StrengthValue>
            <StrengthBar value={dim.value} />
          </StrengthItem>
        ))}
      </StrengthsGrid>
    </DeepDiveContainer>
  );
};

export default DeepDiveSection;